// Batch G — Metrics view.
//
// Side-by-side Cap rate · CoC · DSCR · Monthly CF, asking vs counter
// for every deal. Per-row metrics come from POST /api/intelligence/
// deal-at-price computed in the parent and passed as `rows` prop:
//
//   rows = [{ deal: <DealPublic>, at_asking: {metrics}, at_counter: {metrics} }, ...]
//
// Δ column shows the absolute difference with a colored chevron — green
// when counter improves on asking.

import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { useFmt } from "@/lib/useFmt";
// Phase 36 Batch Z follow-up — P1 fix: stage editor must be reachable
// from every Portfolio view-tab (Prices/Metrics/Verdicts), not just the
// Basic tab. Mirrors the inline pattern from Portfolio.jsx (lines 857-861).
import StageBadge from "@/components/pipeline/StageBadge";

function Delta({ before, after, unit = "" }) {
  const a = Number(before || 0);
  const b = Number(after || 0);
  const d = b - a;
  if (Math.abs(d) < 0.005) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Minus className="h-3 w-3" /> 0
      </span>
    );
  }
  const positive = d > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        positive ? "text-emerald-700" : "text-rose-700"
      }`}
    >
      {positive ? (
        <ArrowUp className="h-3 w-3" />
      ) : (
        <ArrowDown className="h-3 w-3" />
      )}
      {positive ? "+" : ""}
      {Math.abs(d).toFixed(2)}
      {unit}
    </span>
  );
}

function MetricCell({ before, after, unit = "", money }) {
  const fmt = useFmt();
  const fmtVal = (v) =>
    money ? fmt.money(v || 0, { primaryOnly: true, cents: true }) : `${(v || 0).toFixed(2)}${unit}`;
  return (
    <div className="flex flex-col items-end gap-0.5 tabular-nums">
      <span className="text-xs text-slate-400">{fmtVal(before)}</span>
      <span className="text-sm font-semibold text-slate-900">{fmtVal(after)}</span>
      <Delta before={before} after={after} unit={money ? "" : unit} />
    </div>
  );
}

export default function IntelligenceMetricsTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div
      data-testid="metrics-view"
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2 text-left font-semibold">Property</th>
            <th className="px-4 py-2 text-right font-semibold">Cap rate</th>
            <th className="px-4 py-2 text-right font-semibold">CoC</th>
            <th className="px-4 py-2 text-right font-semibold">DSCR</th>
            <th className="px-4 py-2 text-right font-semibold">Monthly CF</th>
            {/* Phase 36 Batch Q — Payback at counter (or asking when no
                counter set). Single-value column (not a Δ chevron) since
                payback is ratio-derived and the asking-vs-counter delta
                gets absurd when one side is "Never". */}
            <th className="px-4 py-2 text-right font-semibold">Payback</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const d = r.deal;
            const a = r.at_asking?.metrics || {};
            const b = r.at_counter?.metrics || {};
            return (
              <tr
                key={d.id}
                data-testid={`metrics-row-${d.id}`}
                className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/40"
              >
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center gap-2">
                    <div className="truncate text-sm font-medium text-slate-900">
                      {d.property?.address || "—"}
                    </div>
                    {d.pipeline_stage ? (
                      <StageBadge
                        stage={d.pipeline_stage}
                        dealId={d.id}
                        editable
                        size="sm"
                      />
                    ) : null}
                  </div>
                  <div className="text-xs text-slate-500">
                    {d.property?.city ? `${d.property.city}, ${d.property.state}` : "—"}
                  </div>
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <MetricCell before={a.cap_rate} after={b.cap_rate} unit="%" />
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <MetricCell before={a.coc_return} after={b.coc_return} unit="%" />
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <MetricCell before={a.dscr} after={b.dscr} unit="x" />
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <MetricCell before={a.monthly_cash_flow} after={b.monthly_cash_flow} money />
                </td>
                {/* Phase 36 Batch Q — Payback. Single value, not Δ
                    chevron. "Never" when CF ≤ 0 at this price. */}
                <td className="px-4 py-3 text-right align-top" data-testid={`metrics-payback-${d.id}`}>
                  {(() => {
                    const pp = b.payback_period_years ?? a.payback_period_years ?? null;
                    if (pp == null) {
                      return <span className="text-xs text-slate-400">Never</span>;
                    }
                    return (
                      <span className="tabular-nums text-sm font-semibold text-slate-900">
                        {Number(pp).toFixed(1)}
                        <span className="ml-0.5 text-[10px] font-normal text-slate-400">yrs</span>
                      </span>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
